/**
 * game-guoji-xiangqi.js —— 学习乐园 · 国际象棋人机对战（棋盘 UI + 对局流程）
 * ---------------------------------------------------------------------------
 * 三层分工（谁都不越界）：
 *   vendor/chesslib.min.js  规则层：合法着法、FEN/PGN（本文件动态注入，见 ensureRules）
 *   chess-engine.js         计算层：五项评估 + 搜索选棋 + 讲解一步棋
 *   本文件                  界面层：棋盘渲染、点选走子、行子与回看、本机存档、PGN 导出
 *
 * 纪律：**全程零网络请求**——规则库是同源静态文件（注入它不算「上传」，与台账按需注入
 * 表格库同一口径）；对局只写 localStorage；动态内容一律 createElement + textContent。
 *
 * 走子用「点自己的子 → 点亮出的落点」而不是拖拽：手机上拖拽容易误触，键盘用户也够得着
 * （64 格都是 button，Tab 能走一遍）。代价是没有拖拽手感，M1 认这个取舍。
 */
(function (global) {
  'use strict';

  var doc = global.document;
  var SVG_NS = 'http://www.w3.org/2000/svg';

  /* 公共小件出口 ST_UI 由 study.js 提供，footer 里它的 <script> 排在本文件之前——defer 按文档
     顺序执行，所以本文件跑起来时 ST_UI 已就位。以前顺序反了，boot() 当场执行（defer 脚本运行
     时 readyState 已是 interactive）取到的是空对象，toast 这类小件在真浏览器里静默失效。 */
  var UI = null;
  function el(tag, cls) {
    var n = doc.createElement(tag);
    if (cls) { n.className = cls; }
    return n;
  }
  function span(cls, text) {
    var n = el('span', cls);
    n.textContent = text;
    return n;
  }
  function icon(name) {
    var svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'tool-icon');
    svg.setAttribute('aria-hidden', 'true');
    var use = doc.createElementNS(SVG_NS, 'use');
    use.setAttribute('href', '#i-' + name);
    svg.appendChild(use);
    return svg;
  }

  var RULES_SRC = '/static/study/vendor/chesslib.min.js';
  var STORE_KEY = 'study-chess-guoji-xiangqi';
  var START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  var GLYPH = { p: '♟', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚' };
  var PIECE_CN = { p: '兵', n: '马', b: '象', r: '车', q: '后', k: '王' };
  var FILES = 'abcdefgh';

  var root = doc.querySelector('[data-st-game]');
  var E = {};

  /* ---------------- 规则库动态注入（并发共享一次，失败如实说明） ---------------- */
  var rulesPending = null;
  function ensureRules(done) {
    if (global.ChessLib && global.ChessLib.Chess) { done(null); return; }
    if (rulesPending) { rulesPending.push(done); return; }
    rulesPending = [done];
    var s = doc.createElement('script');
    var ver = (root.getAttribute('data-st-ver') || '').replace(/[^0-9a-zA-Z.]/g, '');
    s.src = RULES_SRC + (ver ? '?v=' + ver : '');
    s.onload = function () { settle(global.ChessLib && global.ChessLib.Chess ? null : 'fail'); };
    s.onerror = function () { settle('fail'); };
    (doc.head || doc.documentElement).appendChild(s);
  }
  function settle(err) {
    var cbs = rulesPending || [];
    rulesPending = null;
    for (var i = 0; i < cbs.length; i++) { cbs[i](err); }
  }

  /* ---------------- 音效：WebAudio 现场合成（与琴谱页同口径，零采样零请求） ----------------
   * 落子 = 木质「嗒」（噪声脉冲过带通 + 低频箱体共鸣）；吃子 = 更低更重的双击；
   * 将军 = 轻警示双音；将杀 = 终止音型（你赢上行大三和弦 / 你输下行小调三音）。
   * 合成参数没有采样比对基准，以「不刺耳、有区分度」为准。无 AudioContext（jsdom 等）
   * 全部静默可玩；开关存本机，默认开。 */
  var SFX = {
    ctx: null, master: null, on: true,
    LS: 'study-chess-sound',
    load: function () {
      try { this.on = global.localStorage.getItem(this.LS) !== 'off'; } catch (e) { this.on = true; }
    },
    save: function () {
      try { global.localStorage.setItem(this.LS, this.on ? 'on' : 'off'); } catch (e) { /* 隐私模式：不保存也照玩 */ }
    },
    ok: function () {
      if (!this.on) { return false; }
      if (this.ctx) { return true; }
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) { return false; }
      try {
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.6;
        this.master.connect(this.ctx.destination);
      } catch (e) { this.ctx = null; return false; }
      return true;
    },
    ensure: function () {
      if (!this.ok()) { return false; }
      if (this.ctx.state === 'suspended') {
        var p = this.ctx.resume();
        if (p && p['catch']) { p['catch'](function () { /* 非手势环境：这次不响，下次手势里再试 */ }); }
      }
      return true;
    },
    warm: function () { this.ensure(); },
    /* 木质敲击：短噪声脉冲过带通（频谱冲击）+ 低频正弦（棋盘箱体共鸣）。at = 音频时间轴偏移 */
    knock: function (freq, vol, decay, at) {
      var ctx = this.ctx;
      var t = ctx.currentTime + (at || 0);
      var len = Math.max(1, Math.floor(ctx.sampleRate * 0.06));
      var buf = ctx.createBuffer(1, len, ctx.sampleRate);
      var d = buf.getChannelData(0);
      for (var i = 0; i < len; i++) { d[i] = (Math.random() * 2 - 1) * (1 - i / len); }
      var src = ctx.createBufferSource();
      src.buffer = buf;
      var bp = ctx.createBiquadFilter();
      bp.type = 'bandpass'; bp.frequency.value = freq; bp.Q.value = 1.1;
      var g = ctx.createGain();
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + decay);
      src.connect(bp); bp.connect(g); g.connect(this.master);
      src.start(t);
      var g2 = ctx.createGain();
      g2.gain.setValueAtTime(vol * 0.5, t);
      g2.gain.exponentialRampToValueAtTime(0.001, t + decay * 1.6);
      g2.connect(this.master);
      var o = ctx.createOscillator();
      o.type = 'sine'; o.frequency.value = 150;
      o.connect(g2); o.start(t); o.stop(t + decay * 1.8);
    },
    /* 短音（triangle）：将杀音型与将军警示共用 */
    tone: function (freq, dur, vol, at) {
      var ctx = this.ctx;
      var t = ctx.currentTime + (at || 0);
      var g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(vol, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      g.connect(this.master);
      var o = ctx.createOscillator();
      o.type = 'triangle'; o.frequency.value = freq;
      o.connect(g); o.start(t); o.stop(t + dur + 0.1);
    },
    move: function () {
      if (!this.ensure()) { return; }
      this.knock(1000, 0.5, 0.08);
    },
    capture: function () {
      if (!this.ensure()) { return; }
      this.knock(520, 0.65, 0.13);
      this.knock(1250, 0.3, 0.09, 0.05);
    },
    checkSfx: function () {
      if (!this.ensure()) { return; }
      this.tone(660, 0.07, 0.22, 0);
      this.tone(880, 0.09, 0.22, 0.09);
    },
    mate: function (won) {
      if (!this.ensure()) { return; }
      var seq = won
        ? [[261.63, 0], [329.63, 0.12], [392, 0.24], [523.25, 0.36]]   // C4 E4 G4 C5：上行大三和弦
        : [[329.63, 0], [261.63, 0.16], [220, 0.32]];                  // E4 C4 A3：下行小三音
      for (var i = 0; i < seq.length; i++) { this.tone(seq[i][0], 0.42, 0.34, seq[i][1]); }
    },
    /** 从 SAN 判这一步配什么声：# 将杀 > + 将军 > x 吃子 > 普通落子。
     *  won 只对将杀有意义：走完这步被将杀的一方 turn() 就是它——黑被杀 = 你赢。 */
    fromSan: function (san, won) {
      if (!this.on || !san) { return; }
      if (san.indexOf('#') >= 0) { this.mate(won); }
      else if (san.indexOf('+') >= 0) { this.checkSfx(); }
      else if (san.indexOf('x') >= 0) { this.capture(); }
      else { this.move(); }
    }
  };

  /* ---------------- 对局状态 ---------------- */
  var game = null;          // 规则层实例
  var level = 1;
  var plies = [];           // { san, mover, from, to, explain }
  var sel = null;           // 选中格名，如 'e2'
  var hits = null;          // 选中格的合法落点：目标格名 -> verbose 着法
  var paintedPly = null;    // 上一次画到第几个半回合（null = 还没画过）——决定这一步该不该飞子
  var hintSquares = null;   // 「给个提示」高亮的 {from,to}
  var pendingPromo = null;  // 等升变选择的 {from,to}
  var busy = false;         // 对手正在算
  var reviewAt = null;      // 回看中的行子下标（null = 就在最新一手）
  var replayTimer = null;   // 「回放这局」的定时器（非 null = 回放中）
  var resigned = false;
  var savedNote = '';

  /* ---------------- 本机存档 ---------------- */
  function save() {
    try {
      global.localStorage.setItem(STORE_KEY, JSON.stringify({
        v: 1, level: level, resigned: resigned,
        sans: plies.map(function (p) { return p.san; })
      }));
    } catch (e) { /* 隐私模式或配额满：这局照下，只是不跨页保存 */ }
  }
  function lookupMove(g, san) {
    var list = g.moves({ verbose: true });
    for (var i = 0; i < list.length; i++) {
      if (list[i].san === san) { return list[i]; }
    }
    return null;
  }
  /** 恢复上一局：SAN 逐个重放。讲解不存盘（存了也和引擎版本绑定），恢复时按同一份
   *  评估当场重算，成本是每步两次静态评估，比一次搜索便宜得多。任一步重放失败就整局作废。 */
  function restore() {
    var raw = null;
    try { raw = global.localStorage.getItem(STORE_KEY); } catch (e) { return; }
    if (!raw) { return; }
    var data = null;
    try { data = JSON.parse(raw); } catch (e) { return; }
    if (!data || !data.sans || !data.sans.length) { return; }

    var replay = new global.ChessLib.Chess(START_FEN);
    var built = [];
    for (var i = 0; i < data.sans.length; i++) {
      var verbose = lookupMove(replay, data.sans[i]);
      if (!verbose) { return; }                      // 与规则不符（存档被手改过）→ 放弃
      built.push({
        san: verbose.san, mover: verbose.color, from: verbose.from, to: verbose.to,
        explain: global.STChessEngine.annotate(replay, verbose)
      });
      replay.move(data.sans[i]);
    }
    game = replay;
    plies = built;
    level = data.level || 1;
    resigned = !!data.resigned;
    savedNote = '已从本机恢复上一局，走到第 ' + Math.ceil(plies.length / 2) + ' 回合'
      + '（棋局只存在这台设备的浏览器里，换设备就没了）';
  }

  /* ---------------- 渲染 ---------------- */
  /** 回看时从开局重放到那一步：几十次合法着法重放，纯本地，成本可忽略。
   *  返回规则层实例而不是棋盘数组——评估条与棋盘共用这一个局面，
   *  回看/回放时评估条跟着重演（lichess 复盘口径），而不是停在最新局面。 */
  function positionNow() {
    if (reviewAt === null) { return game; }
    var g = new global.ChessLib.Chess(START_FEN);
    for (var i = 0; i <= reviewAt; i++) {
      try { g.move(plies[i].san); } catch (e) { break; }
    }
    return g;
  }
  function checkedKing(board) {
    /* 高亮「正在被将军的那个王」。当前局面直接问规则层；回看时用那一步的讲解结果
       （annotate 已经算过走完之后的应将状态，check 为真 = 接棋一方的王正在被将军）。 */
    var mover = reviewAt === null ? null : plies[reviewAt].mover;
    var inCheck = reviewAt === null
      ? game.inCheck()
      : !!(plies[reviewAt].explain && plies[reviewAt].explain.check);
    if (!inCheck) { return null; }
    var turn = mover ? (mover === 'w' ? 'b' : 'w') : game.turn();
    for (var r = 0; r < 8; r++) {
      for (var c = 0; c < 8; c++) {
        var pc = board[r][c];
        if (pc && pc.type === 'k' && pc.color === turn) {
          return FILES.charAt(c) + String(8 - r);
        }
      }
    }
    return null;
  }
  function renderBoard() {
    var board = positionNow().board();
    var checkSq = checkedKing(board);
    var box = E.board;
    /* shown = 这次画的是第几个半回合（回看时是点选的那一步，否则就是最新一手）。
       起点/落点的高亮和飞子都以它为准，而不是游戏的最后一手——回看时圈的必须是
       这一步自己的来路，不然局面是第 5 回合、亮的却是终局那步。 */
    var shown = reviewAt === null ? plies.length - 1 : reviewAt;
    var cue = shown >= 0 ? plies[shown] : null;
    box.textContent = '';
    for (var r = 0; r < 8; r++) {
      for (var c = 0; c < 8; c++) {
        var sq = FILES.charAt(c) + String(8 - r);
        var btn = el('button', 'st-sq' + ((r + c) % 2 === 1 ? ' st-sq--dark' : ''));
        btn.type = 'button';
        btn.setAttribute('data-sq', sq);
        var pc = board[r][c];
        btn.setAttribute('aria-label', sq + (pc
          ? ' ' + (pc.color === 'w' ? '白' : '黑') + PIECE_CN[pc.type] : ' 空'));
        if (c === 0) { btn.appendChild(span('st-sq-coord st-sq-coord--rank', String(8 - r))); }
        if (r === 7) { btn.appendChild(span('st-sq-coord st-sq-coord--file', FILES.charAt(c))); }
        if (pc) {
          var piece = el('span', 'st-piece st-piece--' + pc.color);
          piece.textContent = GLYPH[pc.type];
          btn.appendChild(piece);
        }
        if (cue && (sq === cue.from || sq === cue.to)) { btn.className += ' is-last'; }
        if (hintSquares && (sq === hintSquares.from || sq === hintSquares.to)) { btn.className += ' is-hint'; }
        if (sel === sq) { btn.className += ' is-sel'; }
        if (hits && hits[sq]) {
          btn.className += pc ? ' is-hit is-capture' : ' is-hit';
        }
        if (checkSq === sq) { btn.className += ' is-check'; }
        btn.addEventListener('click', onSquare);
        box.appendChild(btn);
      }
    }
    /* 飞子：只在「比上一次画的正好推进一手」时播。点行子跳转、退一步、选子亮格都不飞
       ——它们是切换视角，不是走子。 */
    if (UI.fly && cue && paintedPly !== null && shown === paintedPly + 1) {
      UI.fly(box, cue.from, cue.to);
      /* 与飞子同条件发声：只在真实推进一手时响（走子 / 回放 / 逐格回看推进），
         点行子跳转、退一步、选子亮格都不响——它们是切换视角，不是走子。 */
      SFX.fromSan(cue.san, game.turn() === 'b');
    }
    paintedPly = shown;
  }

  /* data-ply = 这局里第几个半回合（0 起）：偶数 = 白方（你）的子，奇数 = 黑方（对手）的应手。
     data-ply="0" → 第 1 回合白方；data-ply="1" → 第 1 回合黑方；回合号 = Math.floor(idx/2)+1。 */
  function renderMoves() {
    var box = E.moves;
    box.textContent = '';
    // 列头：左列是你（白）、右列是电脑（黑），与 data-ply 的奇偶一一对应
    if (plies.length) {
      var head = el('li', 'st-move-row st-move-row--head');
      head.appendChild(span('st-move-no', '#'));
      head.appendChild(span('st-move-col', '你（白）'));
      head.appendChild(span('st-move-col', '电脑（黑）'));
      box.appendChild(head);
    }
    for (var i = 0; i < plies.length; i += 2) {
      var row = el('li', 'st-move-row');
      row.appendChild(span('st-move-no', (i / 2 + 1) + '.'));
      for (var k = 0; k < 2; k++) {
        var idx = i + k;
        if (idx >= plies.length) { row.appendChild(el('span', 'st-move-ply')); continue; }
        var p = plies[idx];
        var b = el('button', 'st-move-ply');
        b.type = 'button';
        b.textContent = p.san;
        b.setAttribute('data-ply', String(idx));
        if (moveMark(p)) { b.className += ' ' + moveMark(p); }
        if (reviewAt === idx) { b.className += ' is-cur'; }
        b.title = (p.mover === 'w' ? '你 · ' : '对手 · ') + phrasesText(p);
        b.addEventListener('click', onPly);
        row.appendChild(b);
      }
      box.appendChild(row);
    }
  }
  /** 行子里的红字标记：按「这一步让行棋方在五项评估上亏了多少」分档（口径与讲解气泡一致） */
  function moveMark(p) {
    var loss = p.mover === 'w' ? -p.explain.score : p.explain.score;
    var kind = global.STChessEngine.classify(loss);
    return (kind === 'blunder' || kind === 'mistake') ? 'is-lose' : '';
  }
  function phrasesText(p) {
    var ph = p.explain && p.explain.phrases ? p.explain.phrases : [];
    var d = Math.abs(p.explain.score) / 100;
    return (ph.length ? ph.join('；') : '平静的调动，五项评估基本没变')
      + '（评估 ' + (p.explain.score >= 0 ? '+' : '−') + d.toFixed(1) + '）';
  }
  function onPly(e) {
    // 回放中点行子 = 停下回放、直接跳到那一步
    if (replayTimer) { stopReplay(false); }
    var idx = Number(e.currentTarget.getAttribute('data-ply'));
    // 点最后一步 = 回到当前局面；点中间某步 = 回看那一步之后的棋盘和当时的理由
    reviewAt = (idx === plies.length - 1 || reviewAt === idx) ? null : idx;
    sel = null; hits = null; hintSquares = null;
    render();
    if (reviewAt !== null) {
      showExplain(plies[reviewAt], '回看第 ' + (Math.floor(reviewAt / 2) + 1) + ' 回合');
    }
  }

  function renderEval() {
    var ev = global.STChessEngine.evaluate(positionNow());
    // 评估条中点 = 均势；用 sigmoid 而不是线性缩放，否则多一个车就把条顶满，读不出细节
    var pct = 1 / (1 + Math.exp(-ev.total / 220));
    E.evalFill.style.width = (pct * 100).toFixed(1) + '%';
    var pawns = ev.total / 100;
    E.evalNum.textContent = (pawns >= 0 ? '+' : '−') + Math.abs(pawns).toFixed(1);
    E.material.textContent = materialNote(ev.material);
  }
  function materialNote(material) {
    var d = Math.round(material / 10) / 10;   // 厘兵 → 兵
    if (Math.abs(d) < 0.5) { return '子力持平'; }
    return (d > 0 ? '你（白）多 ' : '对手（黑）多 ') + Math.abs(d).toFixed(1) + ' 个兵';
  }

  function renderStatus() {
    var box = E.status;
    box.className = 'st-status';
    if (replayTimer) {
      box.textContent = '回放中：重演到第 ' + (Math.floor(reviewAt / 2) + 1)
        + ' 回合。点「停止回放」或点棋盘回到最新局面。';
      return;
    }
    var over = gameOverNote();
    if (over) {
      box.className += ' is-over';
      box.textContent = over;
      return;
    }
    if (reviewAt !== null) {
      box.textContent = '正在回看第 ' + (Math.floor(reviewAt / 2) + 1)
        + ' 回合之后的局面，点行子里最后一步回到当前。';
      return;
    }
    if (busy) {
      box.textContent = '对手正在算这一步（全部在本地算，没有请求服务器）。';
      return;
    }
    if (game.turn() !== 'w') {
      box.textContent = '轮到黑方。';
      return;
    }
    if (game.inCheck()) {
      box.className += ' is-warn';
      box.textContent = '轮你走（白）· 王被将军了，先把将解开。';
      return;
    }
    box.textContent = '轮你走（白）· 点一个自己的子，再点一个亮出来的落点。';
  }
  function gameOverNote() {
    if (resigned) { return '你认输了。点「开始新的一局」再来一盘，或先导出棋谱看看是怎么输的。'; }
    if (!game.isGameOver()) { return ''; }
    if (game.isCheckmate()) {
      return game.turn() === 'w'
        ? '将杀——这一局对手赢了。行子列表里点任意一步，能回看当时的理由；想看亏在哪，导出棋谱去复盘页分析。'
        : '将杀——你赢了！点行子里的任意一步，可以回看每一步的理由。';
    }
    if (game.isStalemate()) { return '逼和：轮到你的王没被将军，但你没有任何合法着法，判和。'; }
    if (game.isInsufficientMaterial && game.isInsufficientMaterial()) { return '和棋：双方都没有能将杀的子力。'; }
    if (game.isThreefoldRepetition && game.isThreefoldRepetition()) { return '和棋：同一局面第三次出现。'; }
    if (game.isDrawByFiftyMoves && game.isDrawByFiftyMoves()) { return '和棋：50 回合内没有吃子也没有动兵。'; }
    return '和棋。';
  }
  function renderButtons() {
    var over = gameOverNote() !== '';
    var replaying = replayTimer !== null;
    E.undo.disabled = busy || over || plies.length === 0 || replaying;
    E.hint.disabled = busy || over || replaying || reviewAt !== null || game.turn() !== 'w';
    E.resign.disabled = busy || over || plies.length === 0 || replaying;
    E.replay.disabled = busy || plies.length === 0;
    var d = reviewAt === null ? plies.length - 1 : reviewAt;
    E.prev.disabled = busy || replaying || plies.length === 0 || d <= 0;
    E.next.disabled = busy || replaying || reviewAt === null;
    if (E.replayLabel) { E.replayLabel.textContent = replaying ? '停止回放' : '回放这局'; }
    E.export.disabled = plies.length === 0;
    E.copy.disabled = plies.length === 0;
    if (E.review) { E.review.disabled = plies.length === 0; }
  }
  function render() {
    if (!game) { return; }
    renderBoard();
    renderMoves();
    renderEval();
    renderStatus();
    renderButtons();
    E.saved.hidden = !savedNote;
    E.saved.textContent = savedNote;
    // 当前行子始终可见：回看时滚到那一步，最新局面时滚到列表末尾
    var cur = E.moves.querySelector('.is-cur');
    if (cur && cur.scrollIntoView) { cur.scrollIntoView({ block: 'nearest' }); }
    else { E.moves.scrollTop = E.moves.scrollHeight; }
  }

  /* ---------------- 回放这局 ----------------
     从开局按 0.9 秒/半回合自动重演：reviewAt 逐半回合推进，棋盘 / 讲解 / 行子高亮跟着走，
     与「点行子回看」是同一套重放机制，纯本地零请求。回放中再点按钮 = 停止并回到最新局面；
     点行子 = 跳到那一步；点棋盘 = 停止回放。 */
  function stopReplay(toLive) {
    if (replayTimer) { global.clearInterval(replayTimer); replayTimer = null; }
    if (toLive) { reviewAt = null; }
  }
  /* 手动逐格回看：dir = -1 上一步 / +1 下一步。显示中的半回合序号 d：
     实时局面 = 最后一手（len-1）；走到最新一手再「下一步」就回到实时。 */
  function stepPly(dir) {
    if (busy || !plies.length) { return; }
    if (replayTimer) { stopReplay(false); }
    var d = (reviewAt === null ? plies.length - 1 : reviewAt) + dir;
    if (d < 0) { return; }
    if (d >= plies.length - 1) {
      reviewAt = null;                       // 走到最新一手 = 回到实时局面
      sel = null; hits = null; hintSquares = null;
      render();
      return;
    }
    reviewAt = d;
    sel = null; hits = null; hintSquares = null;
    render();
    showExplain(plies[d], '回看第 ' + (Math.floor(d / 2) + 1) + ' 回合');
  }
  function replayGame() {
    if (busy || !plies.length) { return; }
    if (replayTimer) { stopReplay(true); render(); return; }
    sel = null; hits = null; hintSquares = null; pendingPromo = null;
    E.promo.hidden = true;
    var i = -1;
    replayTimer = global.setInterval(function () {
      i += 1;
      if (i >= plies.length) { stopReplay(true); render(); return; }
      reviewAt = i;
      render();
      showExplain(plies[i], '回放第 ' + (Math.floor(i / 2) + 1) + ' 回合');
    }, 900);
    render();
  }

  /* ---------------- 讲解气泡（本栏目的产品底线：每一步都有理由） ---------------- */
  function showExplain(p, headText) {
    var box = E.explain;
    box.textContent = '';
    box.className = 'st-explain';
    var loss = p.mover === 'w' ? -p.explain.score : p.explain.score;
    var kind = global.STChessEngine.classify(loss);
    if (p.mover === 'w') { box.className += ' st-explain--me'; }
    if (kind === 'blunder' || kind === 'mistake') { box.className += ' st-explain--bad'; }

    var head = el('p', 'st-explain-head');
    head.appendChild(icon('lightbulb'));
    var title = headText || (p.mover === 'w' ? '你这一步：' + p.san : '对手为什么走 ' + p.san);
    if (p.mover === 'w' && kind === 'blunder') { title += ' —— 这步亏得有点多'; }
    else if (p.mover === 'w' && kind === 'mistake') { title += ' —— 有更好的选择'; }
    head.appendChild(span('', title));
    box.appendChild(head);

    var ph = p.explain.phrases || [];
    if (ph.length) {
      var list = el('ul', 'st-explain-list');
      for (var i = 0; i < ph.length; i++) {
        var li = el('li');
        li.textContent = ph[i];
        list.appendChild(li);
      }
      box.appendChild(list);
    } else {
      var quiet = el('li');
      quiet.textContent = '五项评估（子力 / 中心 / 发展 / 王的安全 / 威胁）在这一步上几乎都没有增减——是一次平静的调动。';
      var ul = el('ul', 'st-explain-list');
      ul.appendChild(quiet);
      box.appendChild(ul);
    }
  }

  /* ---------------- 走子 ---------------- */
  function onSquare(e) {
    if (busy || gameOverNote() !== '') { return; }
    // 回放中点棋盘 = 停止回放、回到最新局面（不用先去找「停止回放」按钮）
    if (replayTimer) { stopReplay(true); render(); return; }
    if (reviewAt !== null) { return; }
    var sq = e.currentTarget.getAttribute('data-sq');
    // 升变面板亮着时，点棋盘任何地方先收起来（不点它就没法继续，得留退路）
    if (pendingPromo) { cancelPromo(); }
    if (hits && hits[sq]) { commitUser(sq); return; }
    var pc = game.get(sq);
    if (pc && pc.color === 'w') {
      sel = sq;
      hits = {};
      var list = game.moves({ square: sq, verbose: true });
      for (var i = 0; i < list.length; i++) { hits[list[i].to] = list[i]; }
      hintSquares = null;
      renderBoard();
      renderButtons();
      return;
    }
    sel = null; hits = null;
    renderBoard();
  }
  function cancelPromo() {
    pendingPromo = null;
    sel = null; hits = null;
    E.promo.hidden = true;
    renderBoard();
  }
  function commitUser(to) {
    var from = sel;
    var mv = hits[to];
    if (mv && mv.promotion) {
      pendingPromo = { from: from, to: to };
      E.promo.hidden = false;
      return;
    }
    applyUser(from, to, null);
  }
  function applyUser(from, to, promo) {
    var verbose = null, list = game.moves({ verbose: true });
    for (var i = 0; i < list.length; i++) {
      if (list[i].from === from && list[i].to === to) { verbose = list[i]; break; }
    }
    if (!verbose) { sel = null; hits = null; renderBoard(); return; }
    var ex = global.STChessEngine.annotate(game, verbose);
    game.move(promo ? { from: from, to: to, promotion: promo } : { from: from, to: to });
    plies.push({ san: verbose.san, mover: verbose.color, from: from, to: to, explain: ex });
    sel = null; hits = null; hintSquares = null; pendingPromo = null; savedNote = '';
    E.promo.hidden = true;
    save();
    render();
    showExplain(plies[plies.length - 1], null);
    if (gameOverNote() !== '') { return; }
    think();
  }
  /** 对手落子：先让「正在算」画出来，再跑同步搜索（引擎自带时间预算，最坏约 2 秒） */
  function think() {
    busy = true;
    E.thinking.hidden = false;
    renderStatus();
    renderButtons();
    global.setTimeout(function () {
      var r = null;
      try { r = global.STChessEngine.bestMove(game, level); } catch (e) { r = null; }
      busy = false;
      E.thinking.hidden = true;
      if (!r || !r.move) { render(); return; }
      var m = r.move;
      var ex = global.STChessEngine.annotate(game, m);
      game.move(m.promotion ? { from: m.from, to: m.to, promotion: m.promotion }
        : { from: m.from, to: m.to });
      plies.push({ san: m.san, mover: m.color, from: m.from, to: m.to, explain: ex });
      save();
      render();
      showExplain(plies[plies.length - 1], null);
    }, 40);
  }

  /* ---------------- 操作 ---------------- */
  function emptyExplain() {
    E.explain.textContent = '';
    E.explain.className = 'st-explain';
    var p = el('p', 'st-explain-empty');
    p.appendChild(icon('lightbulb'));
    p.appendChild(span('', '对手走完，这里会出现一句它为什么这么走'));
    E.explain.appendChild(p);
  }
  function newGame() {
    stopReplay(false);
    game = new global.ChessLib.Chess(START_FEN);
    plies = []; sel = null; hits = null; hintSquares = null;
    pendingPromo = null; reviewAt = null; busy = false; resigned = false; savedNote = '';
    E.promo.hidden = true;
    emptyExplain();
    save();
    render();
  }
  function undoMove() {
    if (busy || !plies.length) { return; }
    stopReplay(true);
    // 退回到「轮你走」：末步是对手就退两步，是你自己就退一步
    var steps = plies[plies.length - 1].mover === 'b' ? 2 : 1;
    for (var i = 0; i < steps && plies.length; i++) {
      game.undo();
      plies.pop();
    }
    resigned = false; reviewAt = null; sel = null; hits = null; hintSquares = null; savedNote = '';
    save();
    render();
    if (!plies.length) { emptyExplain(); }
  }
  function giveHint() {
    if (busy || game.turn() !== 'w') { return; }
    var r = null;
    try { r = global.STChessEngine.bestMove(game, Math.min(level + 1, 3)); } catch (e) { r = null; }
    if (!r || !r.move) { return; }
    hintSquares = { from: r.move.from, to: r.move.to };
    sel = null; hits = null;
    renderBoard();
    renderButtons();
    var ex = global.STChessEngine.annotate(game, r.move);
    showExplain({ san: r.move.san, mover: 'w', explain: ex }, '提示 · 可以试试');
    var note = el('p', 'st-explain-list');
    note.textContent = '提示是当前强度下引擎的选择，不是唯一的好棋；棋盘上绿框标出的就是它建议的那一步。';
    E.explain.appendChild(note);
  }
  function resignGame() {
    if (busy || gameOverNote() !== '') { return; }
    stopReplay(false);
    resigned = true;
    save();
    render();
  }

  /* ---------------- 棋谱（PGN 是国际象棋的通用文本格式） ---------------- */
  function pgnResult() {
    if (resigned) { return '0-1'; }
    if (game.isCheckmate()) { return game.turn() === 'w' ? '0-1' : '1-0'; }
    if (game.isGameOver()) { return '1/2-1/2'; }
    return '*';
  }
  function buildPgn() {
    var d = new Date();
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    var lv = global.STChessEngine.LEVELS[level] || {};
    /* chess.js 1.4 的 header() 只认 (键, 值) 成对入参：传一个对象会被当成「读全部头部」静默忽略
       —— 冒烟里导出的 PGN 全是 "?" 默认值就是这么来的。逐个键写。 */
    game.header('Event', '学习乐园 · 国际象棋人机对战');
    game.header('Site', global.location ? global.location.href.split('?')[0] : '');
    game.header('Date', d.getFullYear() + '.' + pad(d.getMonth() + 1) + '.' + pad(d.getDate()));
    game.header('White', '你');
    game.header('Black', '电脑（' + (lv.label || '') + '）');
    game.header('Result', pgnResult());
    return game.pgn({ newline: '\n' });
  }
  function exportPgn() {
    var text = buildPgn();
    var name = '学习乐园-国际象棋-' + new Date().toISOString().slice(0, 10) + '.pgn';
    if (!global.Blob || !global.URL || !global.URL.createObjectURL) { copyPgn(); return; }
    var a = doc.createElement('a');
    a.href = global.URL.createObjectURL(new global.Blob([text], { type: 'text/plain;charset=utf-8' }));
    a.download = name;
    doc.body.appendChild(a);
    a.click();
    doc.body.removeChild(a);
    global.setTimeout(function () { global.URL.revokeObjectURL(a.href); }, 2000);
    if (UI && UI.toast) { UI.toast('棋谱已存为 ' + name); }
  }
  function copyPgn() {
    var text = buildPgn();
    if (UI && UI.copy) { UI.copy(text, E.copy); return; }
    if (global.navigator && global.navigator.clipboard) {
      global.navigator.clipboard.writeText(text)['catch'](function () { /* 复制失败就不打扰 */ });
    }
  }
  /* 复盘这一局：把 PGN 经 sessionStorage 交给 /study/guoji-xiangqi-fupan（同站中转，不出浏览器），
     复盘页读到就自动填入并开始分析。sessionStorage 不可用（隐私模式）时退回复制。 */
  function handoffReview() {
    var text = buildPgn();
    try {
      global.sessionStorage.setItem('study-guoji-xiangqi-fupan-handoff', text);
      global.location.href = '/study/guoji-xiangqi-fupan';
      return;
    } catch (e) { /* sessionStorage 被禁：落到复制，用户自己粘过去 */ }
    copyPgn();
    if (UI && UI.toast) { UI.toast('浏览器不允许中转，棋谱已复制，去复盘页粘贴即可'); }
  }

  /* ---------------- 强度选择 ---------------- */
  function bindPicker() {
    var opts = root.querySelectorAll('[data-st-level]');
    var sync = function (picked) {
      for (var k = 0; k < opts.length; k++) {
        var on = opts[k] === picked;
        opts[k].className = 'tool-segmented-btn' + (on ? ' is-active' : '');
        opts[k].setAttribute('aria-checked', on ? 'true' : 'false');
      }
    };
    for (var i = 0; i < opts.length; i++) {
      opts[i].addEventListener('click', function (e) {
        level = Number(e.currentTarget.getAttribute('data-st-level')) || 1;
        sync(e.currentTarget);
        save();
      });
    }
    for (var j = 0; j < opts.length; j++) {
      if (Number(opts[j].getAttribute('data-st-level')) === level) { sync(opts[j]); }
    }
  }

  /* ---------------- 落子音效开关（两档，与本机存档同步） ---------------- */
  function bindSound() {
    SFX.load();
    if (!E.soundBtns || !E.soundBtns.length) { return; }
    var sync = function () {
      for (var i = 0; i < E.soundBtns.length; i++) {
        var lit = E.soundBtns[i].getAttribute('data-st-sound') === (SFX.on ? 'on' : 'off');
        E.soundBtns[i].className = 'tool-segmented-btn' + (lit ? ' is-active' : '');
        E.soundBtns[i].setAttribute('aria-checked', lit ? 'true' : 'false');
      }
    };
    for (var j = 0; j < E.soundBtns.length; j++) {
      E.soundBtns[j].addEventListener('click', function (e) {
        SFX.on = e.currentTarget.getAttribute('data-st-sound') !== 'off';
        SFX.save();
        sync();
        if (SFX.on) { SFX.move(); }   // 开档给一声落子反馈，顺手在手势栈里解锁音频
      });
    }
    sync();
  }

  /* ---------------- 启动 ---------------- */
  function boot() {
    UI = global.ST_UI;
    E = {
      board: pick('board'), promo: pick('promo'), thinking: pick('thinking'), status: pick('status'),
      evalFill: pick('eval-fill'), evalNum: pick('eval-num'), material: pick('material'),
      explain: pick('explain'), moves: pick('moves'), saved: pick('saved'),
      newBtn: pick('new'), undo: pick('undo'), hint: pick('hint'), resign: pick('resign'),
      replay: pick('replay'), replayLabel: pick('replay-label'),
      prev: pick('prev'), next: pick('next'),
      export: pick('export'), copy: pick('copy'), review: pick('review')
    };
    E.soundBtns = root.querySelectorAll('[data-st-sound]');
    if (!E.board || !E.explain || !global.STChessEngine) { return; }

    /* 所有按钮点击先热一次音频（在用户手势栈内 resume AudioContext，之后「回放这局」等
       非手势时机的声音才播得出来）；无音频环境时 warm 是空操作 */
    var on = function (node, fn) {
      if (node) {
        node.addEventListener('click', function (e) { SFX.warm(); fn(e); });
      }
    };
    on(E.newBtn, newGame);
    on(E.undo, undoMove);
    on(E.hint, giveHint);
    on(E.resign, resignGame);
    on(E.replay, replayGame);
    on(E.prev, function () { stepPly(-1); });
    on(E.next, function () { stepPly(1); });
    on(E.export, exportPgn);
    on(E.copy, copyPgn);
    on(E.review, handoffReview);
    var promoBtns = root.querySelectorAll('[data-st-promo-p]');
    for (var i = 0; i < promoBtns.length; i++) {
      promoBtns[i].addEventListener('click', function (e) {
        if (!pendingPromo) { return; }
        var from = pendingPromo.from, to = pendingPromo.to;
        pendingPromo = null;
        E.promo.hidden = true;
        applyUser(from, to, e.currentTarget.getAttribute('data-st-promo-p'));
      });
    }
    emptyExplain();
    bindSound();

    ensureRules(function (err) {
      if (err) {
        E.status.className = 'st-status is-warn';
        E.status.textContent = '棋类规则库没加载上（本机静态文件读取失败）。刷新一次通常就好；'
          + '反复出现说明这个站点的静态资源没部署全。';
        return;
      }
      game = new global.ChessLib.Chess(START_FEN);
      restore();
      bindPicker();
      render();
      if (plies.length) { showExplain(plies[plies.length - 1], null); }
    });
  }
  function pick(name) { return root.querySelector('[data-st-' + name + ']'); }

  if (!root || !doc.querySelector('[data-st-board]')) { return; }
  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
